import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import { useCierreCuentas, ListaCuentas, ResultadosCuentas } from './CierreCuentas';
import './DeleteAssetModal.css';
import './VacantesUI.css';
import './DeleteEmployeeModal.css';

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DeleteEmployeeModal = ({ employee, onClose, onConfirm, puedeGestionarCuentas = false }) => {
  const [options, setOptions] = useState({
    disableADUser: false
  });
  // El puesto que deja no puede quedar en el aire: se reemplaza o se elimina
  const [destinoPuesto, setDestinoPuesto] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [enviarCorreo, setEnviarCorreo] = useState(true);
  // Su último día trabajado: CIMA cierra su silla con esta fecha
  const [fechaEgreso, setFechaEgreso] = useState(hoyISO());

  // Sus cuentas (solo TI): qué tiene, qué se hace con cada una y cómo le fue
  const cierre = useCierreCuentas(employee.id, { habilitado: puedeGestionarCuentas });
  const [fase, setFase] = useState('formulario'); // formulario | aplicando | listo
  const [resumen, setResumen] = useState(null);

  // Establecer opciones por defecto basadas en el empleado
  useEffect(() => {
    if (employee) {
      setOptions(prev => ({
        ...prev,
        disableADUser: !!employee.correo
      }));
    }
  }, [employee]);

  // Los equipos se liberan siempre; aquí solo se muestra cuáles
  const equiposALiberar = [
    employee?.computadora_asignada && (
      employee.computadora_asignada.nombre_equipo ||
      `${employee.computadora_asignada.marca || ''} ${employee.computadora_asignada.modelo || ''}`.trim()
    ),
    employee?.telefono_asignado &&
      `${employee.telefono_asignado.marca || ''} ${employee.telefono_asignado.modelo || ''}`.trim(),
    employee?.tablet_asignada &&
      `${employee.tablet_asignada.marca || ''} ${employee.tablet_asignada.modelo || ''}`.trim(),
  ].filter(Boolean);

  const consultando = puedeGestionarCuentas && cierre.consultando;
  const faltaDestino = puedeGestionarCuentas && cierre.faltaDestino;

  const handleOptionChange = (option) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    let respuesta;
    try {
      respuesta = await onConfirm({
        // Con la sección de cuentas, la red se deshabilita desde su fila
        disableADUser: puedeGestionarCuentas ? false : options.disableADUser,
        destino_puesto: destinoPuesto || null,
        fecha_egreso: fechaEgreso,
        // El aviso (y su correo) sale después de cerrar las cuentas, con lo que pasó en cada una
        aviso_despues: puedeGestionarCuentas,
        comment,
        enviar_correo: enviarCorreo,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo registrar el egreso');
      setLoading(false);
      return;
    }
    const ad = respuesta?.ad_disable;
    setResumen([respuesta?.message || 'Egreso registrado', ad?.message].filter(Boolean).join('. '));
    setFase('aplicando');
    if (puedeGestionarCuentas) {
      const { cuentas, paraJefe } = await cierre.aplicarTodo();
      try {
        const r = await axiosInstance.post(`/api/empleados/${employee.id}/egreso/aviso`, {
          vacante_id: respuesta?.vacante_id || null,
          destino_puesto: destinoPuesto || null,
          enviar_correo: enviarCorreo,
          equipos: respuesta?.equipos_liberados || [],
          cuentas,
          ...paraJefe,
        });
        if (r.data?.jefe) setResumen((x) => `${x}. Se le avisó a su jefe (${r.data.jefe})`);
      } catch {
        setResumen((r) => `${r}. No se pudo enviar el aviso del egreso`);
      }
    }
    setFase('listo');
    setLoading(false);
  };

  const cerrar = fase === 'aplicando' ? () => {} : onClose;

  if (fase !== 'formulario') {
    return (
      <Modal isOpen={!!employee} onClose={cerrar} size="lg">
        <Modal.Header onClose={cerrar}>Egreso de {employee.nombre}</Modal.Header>
        <Modal.Body>
          <div className="egr-resumen">
            <FaCheckCircle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{resumen}</span>
          </div>
          {puedeGestionarCuentas && <ResultadosCuentas cierre={cierre} />}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={onClose} disabled={fase === 'aplicando'}>
            {fase === 'aplicando' ? 'Cerrando cuentas...' : 'Cerrar'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal isOpen={!!employee} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-3">
          <FaExclamationTriangle className="text-amber-500" />
          Confirmar Egreso de Empleado
        </span>
      </Modal.Header>
      <Modal.Body>

        <div className="modal-description">
          <p>¿Confirmas el egreso de <strong>{employee.nombre}</strong>?</p>
          <p>Selecciona las acciones a realizar:</p>
        </div>

        {equiposALiberar.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-amber-800 text-sm">
            Sus equipos quedan disponibles: {equiposALiberar.join(', ')}.
          </div>
        )}

        {puedeGestionarCuentas && (
          <section className="mb-4" aria-labelledby="egreso-cuentas-titulo">
            <p id="egreso-cuentas-titulo" className="egr-titulo">Sus cuentas</p>
            <p className="egr-ayuda">Elige qué pasa con cada una. Se aplica al confirmar el egreso.</p>
            <ListaCuentas cierre={cierre} />
          </section>
        )}

        <div className="options-container">
          {employee.correo && !puedeGestionarCuentas && (
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={options.disableADUser}
                onChange={() => handleOptionChange('disableADUser')}
              />
              <span>Deshabilitar usuario AD</span>
            </label>
          )}

          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={enviarCorreo}
              onChange={() => setEnviarCorreo(!enviarCorreo)}
            />
            <span>Enviar correo según la configuración de destinatarios para Egreso</span>
          </label>
        </div>

        {employee.cargo && (
          <div className="mt-4">
            <p className="font-medium text-gray-800 mb-1">
              ¿Qué pasa con su puesto de {employee.cargo}?
            </p>
            <div className="vac-decision" role="radiogroup" aria-label="Destino del puesto">
              {[
                {
                  valor: 'reemplazar',
                  titulo: 'Se reemplaza',
                  detalle: 'Se abre la vacante del puesto. Quienes le reportan pasan a reportarle a la vacante hasta que alguien ingrese.',
                },
                {
                  valor: 'eliminar',
                  titulo: 'Se elimina el puesto',
                  detalle: 'No se busca a nadie. Quienes le reportan pasan a reportarle a su supervisor.',
                },
              ].map((op) => (
                <label
                  key={op.valor}
                  className={`vac-decision-opcion${destinoPuesto === op.valor ? ' vac-decision-opcion--activa' : ''}`}
                >
                  <input
                    type="radio"
                    name="destino_puesto"
                    value={op.valor}
                    checked={destinoPuesto === op.valor}
                    onChange={() => setDestinoPuesto(op.valor)}
                  />
                  <span>
                    <span className="vac-decision-titulo block">{op.titulo}</span>
                    <span className="vac-decision-detalle block">{op.detalle}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="egr-fecha">
          <label htmlFor="egreso-fecha">Último día trabajado</label>
          <input
            id="egreso-fecha"
            type="date"
            className="input"
            value={fechaEgreso}
            max={hoyISO()}
            onChange={e => setFechaEgreso(e.target.value)}
          />
        </div>

        <div className="mt-4">
          <label htmlFor="egress-comment" className="block font-medium text-gray-800 mb-1">
            Comentario (opcional):
          </label>
          <textarea
            id="egress-comment"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Agrega una nota o razón del egreso..."
            rows={3}
            className="input resize-y"
          />
        </div>

        {error && <div className="error-message">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <button
          className="btn btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          className="btn btn-danger"
          onClick={handleConfirm}
          disabled={loading || consultando || faltaDestino || !fechaEgreso || (!!employee.cargo && !destinoPuesto)}
          title={
            employee.cargo && !destinoPuesto ? 'Indica qué pasa con su puesto'
              : consultando ? 'Consultando sus cuentas'
                : faltaDestino ? 'Elige a quién se redirige su correo'
                  : !fechaEgreso ? 'Indica su último día trabajado'
                    : undefined
          }
        >
          {loading ? 'Procesando...' : 'Confirmar Egreso'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

DeleteEmployeeModal.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nombre: PropTypes.string.isRequired,
    equipo_asignado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    smartphone_asignado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    correo: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  puedeGestionarCuentas: PropTypes.bool,
};

export default DeleteEmployeeModal;
